/**
 * Enhanced Error Boundary with Retry Functionality
 * Catches JavaScript errors and provides user-friendly recovery options
 */

import { t, Trans } from "@lingui/macro";
import { ArrowClockwise, House, Warning } from "@phosphor-icons/react";
import { Button } from "@stylingcv/ui";
import type { ReactNode } from "react";
import React, { Component } from "react";

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
};

type State = {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  retryCount: number;
};

export class EnhancedErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Log error for debugging
    console.error("Enhanced Error Boundary caught an error:", error, errorInfo);
  }

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="w-full max-w-md space-y-6 rounded-lg border bg-card p-8 shadow-lg">
            {/* Error Icon */}
            <div className="flex justify-center">
              <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/20">
                <Warning className="size-8 text-red-600 dark:text-red-400" />
              </div>
            </div>

            {/* Error Message */}
            <div className="space-y-2 text-center">
              <h2 className="text-xl font-semibold text-foreground">
                <Trans>
                  <Trans>Oops! Something went wrong</Trans>
                </Trans>
              </h2>
              <p className="text-sm text-muted-foreground">
                <Trans>
                  We encountered an unexpected error. This has been logged and we'll look into it.
                </Trans>
              </p>
            </div>

            {/* Error Details (Development only) */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="rounded border bg-muted p-3">
                <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                  <Trans>
                    <Trans>Error Details (Development)</Trans>
                  </Trans>
                </summary>
                <pre className="mt-2 overflow-auto text-xs text-red-600">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                className="w-full"
                disabled={this.state.retryCount >= 3}
                onClick={this.handleRetry}
              >
                <ArrowClockwise className="mr-2 size-4" />
                {this.state.retryCount >= 3 ? t`Max Retries Reached` : t`Try Again`}
              </Button>

              <div className="flex space-x-2">
                <Button variant="outline" className="flex-1" onClick={this.handleGoHome}>
                  <House className="mr-2 size-4" />
                  {t`Go Home`}
                </Button>

                <Button variant="outline" className="flex-1" onClick={this.handleReload}>
                  <ArrowClockwise className="mr-2 size-4" />
                  {t`Reload Page`}
                </Button>
              </div>
            </div>

            {/* Retry Count */}
            {this.state.retryCount > 0 && (
              <p className="text-center text-xs text-muted-foreground">
                <Trans>Retry attempts: {this.state.retryCount}/3</Trans>
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export const useErrorBoundary = () => {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error) => {
    setError(error);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return {
    captureError,
    resetError,
  };
};

// Wrapper component for easy usage
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
) => {
  const WrappedComponent = (props: P) => (
    <EnhancedErrorBoundary fallback={fallback}>
      <Component {...props} />
    </EnhancedErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};
